import React, { useCallback, useEffect, useState } from 'react';
import { VStack } from '@televet/kibble-ui/build/chakra';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { CheckboxGroup } from '@televet/kibble-ui/build/components/CheckboxGroup';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { AppointmentPetFragment, useSearchAppointmentPetsLazyQuery } from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateAppointmentDraft, updateAppointmentModalErrors } from 'pages/Appointments/state/appointmentsModalSlice';
import { AppointmentPetCard } from './AppointmentPetCard';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import * as Sentry from '@sentry/react';
import useDebounce from 'shared/hooks/useDebounce';
import { LinkedAutomationUpdates } from '..';
import { Spinner } from '@televet/kibble-ui';

interface AppointmentPetProps {
  updateLinkedAutomations: (updates: LinkedAutomationUpdates) => Promise<void>;
}

export const AppointmentPet = ({ updateLinkedAutomations }: AppointmentPetProps): JSX.Element => {
  const {
    appointmentDraft: { petParentIds, selectedPet },
    errors,
  } = useAppSelector((state) => state.appointmentsModal);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 350);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [searchClinicPets, { data: searchResults, loading: isLoadingSearchResults }] =
    useSearchAppointmentPetsLazyQuery({
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
      onError: (e) => {
        toast({
          title: 'Sorry!',
          description: 'We are having trouble looking up this client.',
          status: 'error',
        });
        Sentry.captureException(e);
      },
    });

  const handleSelectPet = useCallback(
    (pet: AppointmentPetFragment) => {
      dispatch(updateAppointmentDraft({ selectedPet: pet, petId: pet.id, petParentIds: [pet.petParents[0].id] }));
      dispatch(
        updateAppointmentModalErrors(errors.filter((error) => error !== 'missingPet' && error !== 'missingPetParent')),
      );
      setSearchTerm('');
      setShowSearchResults(false);
      updateLinkedAutomations({ petId: pet.id });
    },
    [dispatch, errors, updateLinkedAutomations],
  );

  const handleDeletePet = useCallback(() => {
    dispatch(updateAppointmentDraft({ selectedPet: undefined, petId: undefined, petParentIds: [] }));
    updateLinkedAutomations({ petId: undefined });
  }, [dispatch, updateLinkedAutomations]);

  const handleTogglePetParent = useCallback(
    (value: (string | number)[]) => {
      dispatch(updateAppointmentDraft({ petParentIds: value as string[] }));
      dispatch(updateAppointmentModalErrors(errors.filter((error) => error !== 'missingPetParent')));
    },
    [dispatch, errors],
  );

  useEffect(() => {
    setShowSearchResults(!!debouncedSearchTerm.length);
    if (!debouncedSearchTerm) return;
    searchClinicPets({
      variables: {
        whereInput: {
          name: debouncedSearchTerm,
          petParentNames: debouncedSearchTerm,
        },
        pageSize: 25,
      },
    });
  }, [debouncedSearchTerm, searchClinicPets]);

  return (
    <VStack className="AppointmentModal__PetSearchContainer" pos="relative" w="100%" spacing={4}>
      <TextInput
        className="AppointmentModal__PetSearchInput"
        labelStyle={{ fontWeight: 'bold' }}
        label="Search for a client or patient"
        leftElement={<Icon name="magnifyingGlass" />}
        onChange={(e): void => setSearchTerm(e.target.value)}
        value={searchTerm}
        isClearable
        onClear={(): void => setSearchTerm('')}
        isInvalid={errors.includes('missingPet')}
        errorText="Please select a pet"
        data-testid="appointment-pet-search-input"
      />
      {showSearchResults && (
        <VStack
          className="AppointmentModal__PetSearchResults"
          maxH="300px"
          background="background.default"
          overflow="auto"
          w="100%"
          border="1px solid"
          borderColor="border.default"
          borderRadius="base"
          p={4}
          pos="absolute"
          zIndex="banner"
          shadow="md"
          mt={2}
          top="65px"
          spacing={3}
        >
          {isLoadingSearchResults ? (
            <Spinner />
          ) : !searchResults?.clinicPetSearch.length ? (
            <Text>No results found</Text>
          ) : (
            searchResults?.clinicPetSearch.map((pet) => (
              <AppointmentPetCard key={pet.id} pet={pet} handleSelectPet={handleSelectPet} />
            ))
          )}
        </VStack>
      )}
      {!!selectedPet && (
        <VStack className="AppointmentModal__SelectedPetContainer" w="100%" mt={4} align="flex-start" spacing={4}>
          <AppointmentPetCard pet={selectedPet} handleDeletePet={handleDeletePet} />
          <VStack className="AppointmentModal__SelectedPetParent" align="flex-start">
            <Text fontWeight="bold">Client To Be Contacted</Text>
            <CheckboxGroup
              isInvalid={errors.includes('missingPetParent')}
              errorText="Please select a client to be contacted"
              onChange={handleTogglePetParent}
              direction="column"
              value={petParentIds}
              useInvalidText
              checkboxes={selectedPet?.petParents.map((petParent) => ({
                value: petParent.id,
                isChecked: petParentIds?.includes(petParent.id),
                children: `${petParent?.firstName} ${petParent.lastName}`,
              }))}
            />
          </VStack>
        </VStack>
      )}
    </VStack>
  );
};
